import Vue from 'vue'
import MessageQueue from './MessageQueue'
import { MessageFactory, MessageType, TestMessage } from './Message'
import _ from 'lodash'
import { getSocket } from '@/plugin/socket'
import { MESSAGE_COURSE_END } from '@/components/classroom/lib/MessageId'
import { MessageName } from '@/components/classroom/lib/Message'
import JsonWebToken from 'jsonwebtoken'
import SoundEffect from '@/components/classroom/lib/SoundEffect'

const IDE = window['IDE']

const IClassroom = Vue.extend({
  async created () {
    this.$on('message', this.onMessageHandler)
    this.prepareMessageQueue()
  },
  beforeDestroy () {
    this.$off()
  },
  data () {
    return {
      // user data
      queues: {
        system: new MessageQueue(),
        ai: new MessageQueue(),
        self: new MessageQueue()
      },
      user: null,
      course: null,
      group: null,
      problems: null,
      groupLogs: [],      // 章节学习日志
      classmates: [],
      deskmate: null,

      timeOffsetTotal: 0,      // 当前时间与服务器时间的差值（当前时间 + timeOffset = 服务器时间）
      timeOffsetSamples: 0,      // 当前时间与服务器时间的差值（当前时间 + timeOffset = 服务器时间）

      mySchool: {
        score: 0
      },
      opponent: {         // 竞争学校
        name: '',
        image: '',
        score: 0
      },

      // class data
      io: null,
      ioType: 'message', // 两种类型 socket 连接，消息（message）和系统（sys）的
      python: {},
      focusToMessage: _.throttle(this.$focusToMessage, 100, { leading: true, trailing: true }),
      messageWindowHeight: 0,
      autoplay: {
        timer: null,
        time: 0,
        voice: null,
        autoVoice: true,
        loading: false, // 是否正在请求下一条消息，避免过快请求
        couldContinue: false
      },
      actionLogger: {
        courseStart: 0,
        timer: null,
        bubbles: [],
        message: null
      }
    }
  },
  computed: {
    messages () {
      return Object.keys(this.queues).reduce((ret, queue) => {
        ret.push(...this.queues[queue].messages)
        return ret
      }, []).sort((a, b) => a.time - b.time)
    },
    courseMessages () {
      return this.queues.ai.messages
    },
    lastCourseMessage () {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].name === MessageName.AI || this.messages[i].name === MessageName.TEACHER) {
          return this.messages[i]
        }
      }
      return null
    },
    hasTestMessage () {
      const testMessage = _.findLast(this.messages, { type: MessageType.TEST })
      if (testMessage) {
        if (!TestMessage.isPass(testMessage)) {
          return true
        }
      }
      return false
    },
    hasNextMessage () {
      if (!this.lastCourseMessage) return true
      return this.lastCourseMessage.id !== MESSAGE_COURSE_END
    }
  },
  methods: {
    playSound (sound, rate) {
      new SoundEffect().play(sound, rate)
    },
    async saySomething () {
      // if (!window.speechSynthesis || !this.autoplay.autoVoice) return
      // const synth = window.speechSynthesis
      // synth.cancel()
      // const utterThis = new SpeechSynthesisUtterance(whatToSay)
      // // utterThis.voice = this.voice
      // utterThis.lang = 'zh-CN'
      // utterThis.rate = speed
      // utterThis.pitch = 100
      // synth.speak(utterThis)
    },
    getVoice (voiceName, synth) {
      return new Promise((resolve) => {
        synth.onvoiceschanged = function() {
          const voices = synth.getVoices()
          for (let i = 0; i < voices.length; i++) {
            if (voices[i].name === voiceName) {
              resolve(voices[i])
            }
          }
        }
        synth.getVoices()
      })
    },

    registerClassroom (app) {
      this.$api = app.$api
      this.$noty = app.$noty
    },

    setOpponent (opponent) {
      this.opponent.name = opponent.name
      this.opponent.image = opponent.image
      this.opponent.cookbook = opponent.cookbook
      this.opponent.winNoty = opponent.winNoty
      this.opponent.failNoty = opponent.failNoty
    },

    addScore (score, isOpponent) {
      if (isOpponent) {
        this.opponent.score += score
      } else {
        this.mySchool.score += score
      }
    },

    createMessage (name, type, data, meta) {
      if (!this.group) return null
      const msg = MessageFactory(name, type, data, {
        id: Math.abs(~~(Date.now() * Math.random())),
        time: this.getServerTime(),
        next: meta ? meta.next : ''
      })
      msg.groupId = this.group._id
      return msg
    },

    $focusToMessage (message) {
      if (!message) return
      setTimeout(() => {
        const messageEl = document.getElementById('chat-message-' + message.innerId)
        if (!messageEl) {
          return
        }
        const bounding = messageEl.getBoundingClientRect()
        if (bounding.top >= 0 &&
            bounding.left >= 0 &&
            bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight - 50) &&
            bounding.right <= (window.innerWidth || document.documentElement.clientWidth)) {
          // in the view
          return
        }

        const msgWindow = document.getElementById('message-window')
        if (!msgWindow) return
        const max = Math.max(0, message.offsetTop - 40)
        msgWindow.scrollTo({
          left: 0,
          top: Math.min(max, Math.max(0, message.offsetTop + message.estHeight / 2 - IDE.layout.mainHeight / 2)),
          behavior: 'smooth'
        })
      }, 100)
    },
    registerOnMessageHandler () {
      this.$on('message', this.onMessageHandler)
    },
    getServerTime () {
      const now = Date.now()
      if (Math.abs(this.timeOffsetTotal / this.timeOffsetSamples) < 100) {
        return now
      }
      return now + ~~(this.timeOffsetTotal / this.timeOffsetSamples)
    },
    onMessageHandler (message) {
      let queue
      switch (message.name) {
        case 'system':
          queue = this.queues.system
          break
        case 'teacher':
        case 'ai':
        default:
          queue = this.queues.ai
          if (message.time) {
            this.timeOffsetTotal += new Date(message.time).getTime() - Date.now()
            this.timeOffsetSamples += 1
          }
      }

      if (message.groupId !== this.group._id) {
        // 丢弃其它章节的消息
        return
      }

      this.autoplay.couldContinue = message.type === MessageType.TEXT && message.id !== MESSAGE_COURSE_END

      this.loadMessageActions(message)
      if (queue === this.queues.ai) {
        this.autoRequestNextMessage(message)
      }
      message = queue.new(message.name, message.type, message.data, message)

      // 实时进度保存
      this.playSound('pop1')
      this.focusToMessage(message)
    },
    removeMessageById (id) {
      const index = _.findIndex(this.queues.ai.messages, { id: id })
      if (index > -1) {
        this.queues.ai.messages.splice(index, 1)
      }
    },
    async requestNextMessage (delay = 0, nextId) {
      if (this.autoplay.loading) {
        return
      }
      if (this.autoplay.timer) {
        clearTimeout(this.autoplay.timer)
      }
      if (this.hasTestMessage) {
        this.$noty.info('请完成当前任务后进行下一步学习')
        return false
      }
      if (!this.hasNextMessage) {
        return false
      }

      if (delay) {
        await new Promise((resolve) => {
          setTimeout(() => {
            resolve()
          }, delay)
        })
      }

      const lastMessage = this.lastCourseMessage
      if (lastMessage && lastMessage.status === 'loading') {
        // 上一条消息还在加载中
        return false
      }

      this.autoplay.loading = true
      // 请求第一条消息，一次可能接收到多条消息
      const messages = await this.callRemote('course.next_messages', {
        courseId: this.course._id,
        groupId: this.group._id,
        messageId: lastMessage ? lastMessage.id : void 0,
        nextId
      }) || []
      messages.forEach(m => this.$emit('message', m))

      this.autoplay.loading = false
      return true
    },
    prepareMessageQueue () {
      Object.keys(this.queues).forEach(q => {
        this.queues[q].$on('message-push', this.focusToMessage)
        this.queues[q].$on('message-loaded', this.focusToMessage)
      })
    },
    setIoType (type) {
      this.ioType = type
    },
    async callRemote (method, params) {
      if (!this.io) {
        this.io = await getSocket(this.ioType)
      }
      return new Promise((resolve, reject) => {
        const rejectTimer = setTimeout(() => {
          reject(new Error(method + ': 请求超时'))
        }, 5000)
        this.io.emit(method, params, (data) => {
          clearTimeout(rejectTimer)
          if (data) {
            if (data.error) {
              return reject(new Error(data.error))
            }
          }
          resolve(data)
        })
      })
    },

    login (token) {
      const decoded = JsonWebToken.decode(token)
      const tokenData = decoded.data
      const user = {
        'email': tokenData.email,
        'name': tokenData.name,
        '_id': parseInt(tokenData.id),
        'roles': tokenData.roles,
        'type': tokenData.type,
        'last_login': tokenData.last_login,
        'token': token
      }

      this.user = {
        bubble: '',
        showBubble: false,
        seat: 0,
        ...user
      }
    },

    // 加载新课程
    async enterCourse (course) {
      this.course = course
    },

    // 加载课堂
    async enterGroup (group) {
      const groupInfo = await this.$api.get(`/student/courses/${this.course._id}/groups/${group._id}`)
      this.group = group
      this.$set(group, 'content', groupInfo.content)
      this.$set(group, 'commits', groupInfo.commits)
      this.clearQueue()

      this.classmates = []
      this.deskmate = null

      this.opponent.name = ''
      this.opponent.image = ''
      this.opponent.score = 0
      this.mySchool.score = 0

      this.actionLogger.courseStart = Date.now()
      this.messageWindowHeight = 0

      setTimeout(async () => {
        this.problems = await this.$api.get(`/student/courses/${this.course._id}/groups/${this.group._id}/problems`)
        this.$api.get(`/student/courses/${this.course._id}/groups/${this.group._id}/classmates_commits`).then((data) => {
          const classmates = data.map((user, i) => {
            user.sort_index = ~~(Math.random() * 1000000)
            user.bubble = ''
            user.showBubble = false
            user.seat = i
            user.isOpponent = (i % 4 === 2) || (i % 4 === 3)
            user.problems = user.problems.reduce((ret, p) => {
              ret[p._id] = p
              return ret
            }, {})
            if (!user.name) {
              user.name = '无名者'
            }
            if (!user.head_image) {
              user.head_image = `https://python123.io/images/avatars/P${user.user_id % 9 + 1}.png`
              if (i === 0) {
                user.head_image = `https://python123.io/images/avatars/P0.png`
              }
            }
            return user
          })

          this.classmates = [classmates[0], ...classmates.slice(1).sort((a, b) => a.sort_index - b.sort_index)]
          this.$set(this.user, 'head_image', this.classmates[0].head_image)
        })

        if (!this.group.done) {
          // 进入教室
          // 请求第一条课程消息
          setTimeout(() => {
            this.requestNextMessage()
          }, 400)
        }
      }, 100)
    },

    // 选定同桌
    async setDeskmate (deskmate) {
      if (!deskmate) {
        this.deskmate = null
        return
      }
      // 交换位置
      this.classmates[1].seat = deskmate.seat
      deskmate.seat = 1
      this.classmates.sort((a, b) => {
        return a.seat - b.seat
      })
      this.deskmate = deskmate
    },

    clearQueue () {
      this.queues.system.clear()
      this.queues.self.clear()
      this.queues.ai.clear()
    },

    getSentenceLen (str) {
      try {
        // 先将回车换行符做特殊处理
        str = str.replace(/(\r\n+|\s+| +)/g, '龘')
        // 处理英文字符数字，连续字母、数字、英文符号视为一个单词

        // eslint-disable-next-line no-control-regex
        str = str.replace(/[\x00-\xff]/g, 'm')
        // 合并字符m，连续字母、数字、英文符号视为一个单词
        str = str.replace(/m+/g, '*')
        // 去掉回车换行符
        str = str.replace(/龘+/g, '')
        // 返回字数
        return str.length
      } catch (e) {
        // pass
      }
      return 0
    },

    // 自动请求下一条消息
    autoRequestNextMessage (message) {
      if (this.autoplay.timer) {
        clearTimeout(this.autoplay.timer)
      }
      if (!this.hasNextMessage || message.id === MESSAGE_COURSE_END) {
        return
      }
      let delay = -1
      if (message.type === MessageType.TEXT) {
        if (Object.prototype.hasOwnProperty.call(message.data, 'time') && message.data.time >= 0) {
          delay = Math.min(99, message.data.time) * 1000
        } else {
          delay = Math.max(this.getSentenceLen(message.data.text) / 5 * 1000, 1000)
        }
      }
      if (message.type === MessageType.IMAGE) {
        delay = 1500
      }

      if (delay >= 0) {
        this.autoplay.timer = setTimeout(() => {
          this.autoplay.timer = null
          this.requestNextMessage()
        }, delay)
      }
    },

    // 显示右侧面板气泡消息
    showUserBubble (userIndex, bubble, save = false, timeout = 5000) {
      this.playSound('pop' + ~~(Math.random() * 11 + 1))

      this.classmates[userIndex].bubble = bubble
      if (this.classmates[userIndex].timer) {
        return
      }

      this.classmates[userIndex].showBubble = true
      this.classmates[userIndex].timer = setTimeout(() => {
        this.classmates[userIndex].showBubble = false
        this.classmates[userIndex].timer = null
      }, timeout)

      if (userIndex === 0 && save) {
        // 用户自己发的消息，记录下来
        const log = this.createLog(this.lastCourseMessage)
        log.data.bubbles.push(bubble, Date.now() - this.lastCourseMessage.time)
      }
    },

    // 创建用户动作日志
    createLog (message) {
      let log = _.find(this.groupLogs, { _id: message.id })
      if (!log) {
        log = {
          _id: message.id,
          data: {
            actions: null,
            bubbles: []
          }
        }
        this.groupLogs.push(log)
      }
      return log
    },

    // 将消息关联的动作加入动作列表
    loadMessageActions (message) {
      clearTimeout(this.actionLogger.timer)
      this.actionLogger.timer = null
      const bubbles = []
      this.classmates.forEach(user => {
        if (!user.group_logs) return
        user.group_logs.forEach(m => {
          if (m._id === message.id && m.data.bubbles) {
            for (let i = 0; i < m.data.bubbles.length; i += 2) {
              bubbles.push({
                user_id: user.user_id,
                bubble: m.data.bubbles[i],
                time: m.data.bubbles[i + 1]
              })
            }
          }
        })
      })
      this.actionLogger.bubbles = bubbles.sort((a, b) => a.time - b.time)
      this.actionLogger.message = message
      this.playMessageAction()
    },

    // 循环播放动作列表，如气泡消息等
    playMessageAction () {
      const current = Date.now() - new Date(this.actionLogger.message.time).getTime()
      while (this.actionLogger.bubbles.length && this.actionLogger.bubbles[0].time < current) {
        this.showUserBubble(this.actionLogger.bubbles[0].user_id, this.actionLogger.bubbles[0].bubble)
        this.actionLogger.bubbles.shift()
      }

      if (this.actionLogger.bubbles.length) {
        this.actionLogger.timer = setTimeout(this.playMessageAction, Math.max(this.actionLogger.bubbles[0].time - current))
      }
    }
  }
})

export default new IClassroom()
